import { useEffect, useState } from "react";
import { Share, Plus, X, Download } from "lucide-react";

/**
 * Tiny PWA install banner for Omega ZI AI.
 *
 *  • Android / Chromium  — listens for the `beforeinstallprompt` event and
 *    shows a single "Install Omega ZI" button. Clicking calls `prompt()` and
 *    we never re-show after the user accepts or dismisses (localStorage).
 *  • iOS Safari — `beforeinstallprompt` is not supported, so we sniff
 *    standalone/iOS and show a one-line "Tap Share → Add to Home Screen"
 *    coachmark instead. Same dismiss persistence.
 *  • Already-installed users (display-mode: standalone OR iOS standalone)
 *    never see the banner.
 *  • Banner only shows on mobile widths so desktop stays untouched.
 */
const DISMISS_KEY = "zi_pwa_banner_dismissed_v1";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function PwaInstallBanner() {
  const [deferred, setDeferred] = useState(null);   // beforeinstallprompt event
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState("android"); // "android" | "ios"

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (isStandalone()) return;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (!isMobile) return;

    let androidTimer = null;
    let iosTimer = null;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVariant("android");
      // small delay so we don't fight the splash
      androidTimer = setTimeout(() => setShow(true), 1500);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isIOS()) {
      // iOS won't fire beforeinstallprompt — show the share-sheet hint instead.
      setVariant("ios");
      iosTimer = setTimeout(() => setShow(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (androidTimer) clearTimeout(androidTimer);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch { /* user dismissed */ }
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: "calc(12px + env(safe-area-inset-bottom))",
        maxWidth: 460,
        margin: "0 auto",
        zIndex: 9000,
        background: "rgba(16,20,28,0.90)",
        backdropFilter: "blur(22px) saturate(1.6)",
        WebkitBackdropFilter: "blur(22px) saturate(1.6)",
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: 18,
        padding: "13px 13px 13px 15px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 16px 44px rgba(0,0,0,0.5)",
        color: "#f4f7fb",
        animation: "pwaUp 420ms cubic-bezier(.2,.9,.3,1)",
      }}
    >
      <style>{`@keyframes pwaUp{from{opacity:0;transform:translateY(28px);}to{opacity:1;transform:none;}}`}</style>
      <img
        src="/icon-192.png"
        alt="Omega ZI"
        style={{ width: 42, height: 42, flex: "0 0 42px", borderRadius: 11, boxShadow: "0 4px 12px rgba(0,0,0,0.35)" }}
      />

      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: -0.2 }}>Install Omega ZI</div>
        {variant === "ios" ? (
          <div style={{ fontSize: 11.5, opacity: 0.78, marginTop: 3, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            Tap <Share size={13} style={{ verticalAlign: "middle" }} /> then
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontWeight: 700, color: "#fff" }}>
              <Plus size={12} /> Add to Home Screen
            </span>
          </div>
        ) : (
          <div style={{ fontSize: 11.5, opacity: 0.78, marginTop: 3 }}>
            Full-screen · faster loads · offline-ready
          </div>
        )}
      </div>

      {variant === "android" && deferred && (
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "#0A84FF",
            color: "#fff", border: "none", padding: "9px 15px",
            borderRadius: 999, fontSize: 12.5, fontWeight: 700,
            cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit", flexShrink: 0,
          }}
        ><Download size={14} /> Install</button>
      )}
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss install banner"
        style={{
          width: 30, height: 30, borderRadius: 999, flexShrink: 0, cursor: "pointer",
          background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
          color: "rgba(255,255,255,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      ><X size={15} /></button>
    </div>
  );
}
